import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2, Quote, Plus, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Seed letters of support (rendered before any user-submitted letters arrive).
// These represent realistic, illustrative endorsements aligned with Bridge's
// proposed referral network. Replace with verified signed letters as they
// come in from real partners.
const SEED_LETTERS = [
  {
    id: "seed-1",
    author_name: "Faculty endorsement template",
    author_title: "Dean of Workforce Education (illustrative)",
    organization: "Lee College — Baytown",
    quote:
      "Our high-demand certificate pipelines — HVAC, welding, process technology, allied health — already exist. What the community has lacked is a partner who walks the student through enrollment, childcare, and rent stabilization. Baytown Bridge is exactly that bridge. We are ready to formalize the referral protocol.",
  },
  {
    id: "seed-2",
    author_name: "Hospital partnership template",
    author_title: "Director of Community Health (illustrative)",
    organization: "Houston Methodist Baytown",
    quote:
      "Our campus expansion will require hundreds of skilled-support hires across the next 24 months. A program that pre-prepares Baytown residents — and follows their families home through Medicare navigation and home health — is a force multiplier for both our workforce and our discharge outcomes.",
  },
  {
    id: "seed-3",
    author_name: "Faith leader endorsement template",
    author_title: "Senior Pastor (illustrative)",
    organization: "A Baytown congregation",
    quote:
      "Every Sunday I see the same single mother, the same struggling senior. They do not need another flyer or another hotline number — they need a person who knows their name and shows up on Tuesday. The Bridge model is the embodiment of how the church and the city should have been working together all along.",
  },
];

export default function LettersOfSupport() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/letters`)
      .then((res) => {
        if (mounted) setLetters(res.data || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const combined = [...letters, ...SEED_LETTERS];

  const handleSubmitted = (newLetter) => {
    setLetters((prev) => [newLetter, ...prev]);
    setOpen(false);
  };

  return (
    <section
      id="letters"
      data-testid="section-letters"
      className="bg-[#0A192F] text-[#F9F7F1] py-16 lg:py-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                06 — Letters of Support
              </span>
            </div>
            <h2 className="font-display text-3xl lg:text-4xl text-[#F9F7F1] leading-tight">
              The coalition forming around Baytown Bridge.
            </h2>
            <p className="mt-4 text-[#CBD5E1] leading-relaxed max-w-2xl">
              Partner endorsements from Lee College, Houston Methodist Baytown,
              faith communities, and elected officials. Templates below are
              illustrative — real signed letters replace them as the coalition
              forms.
            </p>
          </div>
          <Button
            data-testid="letter-add-btn"
            onClick={() => setOpen(true)}
            className="rounded-none bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-11 px-5 font-mono text-xs uppercase tracking-widest"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Your Letter
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#CBD5E1] font-mono text-xs">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading letters…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15">
            {combined.map((l, i) => (
              <article
                key={l.id || i}
                data-testid={`letter-${i}`}
                className="bg-[#15243F] p-6 lg:p-7 flex flex-col"
              >
                <Quote className="h-6 w-6 text-[#D4AF37] mb-4" />
                <p className="text-[#F9F7F1] leading-relaxed text-sm flex-1 italic">
                  &ldquo;{l.quote}&rdquo;
                </p>
                <div className="mt-6 pt-5 border-t border-[#F9F7F1]/15">
                  <div className="font-display text-base text-[#F9F7F1]">{l.author_name}</div>
                  {l.author_title && (
                    <div className="text-xs text-[#CBD5E1] mt-0.5">{l.author_title}</div>
                  )}
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mt-2">
                    {l.organization}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {open && <LetterDialog onClose={() => setOpen(false)} onSubmitted={handleSubmitted} />}
    </section>
  );
}

function LetterDialog({ onClose, onSubmitted }) {
  const [form, setForm] = useState({
    author_name: "",
    author_title: "",
    organization: "",
    email: "",
    quote: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author_name || !form.organization || !form.email || !form.quote) {
      toast.error("Please complete name, organization, email, and the quote.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/letters`, form);
      toast.success("Thank you. Your letter of support has been received.");
      onSubmitted(res.data);
    } catch (err) {
      toast.error("Unable to submit right now. Please email george.broughton@sterilemate.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="letter-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[#0A192F]/85 backdrop-blur-sm"
      />
      <form
        onSubmit={submit}
        data-testid="letter-form"
        className="relative w-full max-w-2xl bg-[#F9F7F1] text-[#0A192F] p-7 lg:p-9 max-h-[90vh] overflow-y-auto"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="letter-dialog-close"
          className="absolute top-4 right-4 p-2 text-[#475569] hover:text-[#0A192F]"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-2">
          Letter of Support
        </div>
        <h3 className="font-display text-2xl lg:text-3xl mb-1">
          Stand publicly with the Baytown Bridge.
        </h3>
        <p className="text-sm text-[#475569] mb-6">
          We will follow up to verify and to request a formal signed letter on
          your letterhead for the city briefing packet.
        </p>

        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Your Name" required>
              <Input
                data-testid="letter-input-name"
                value={form.author_name}
                onChange={update("author_name")}
                className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
              />
            </Field>
            <Field label="Title / Role">
              <Input
                data-testid="letter-input-title"
                value={form.author_title}
                onChange={update("author_title")}
                placeholder="Director, Pastor, Council Member, etc."
                className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Organization" required>
              <Input
                data-testid="letter-input-org"
                value={form.organization}
                onChange={update("organization")}
                className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
              />
            </Field>
            <Field label="Email" required>
              <Input
                data-testid="letter-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
              />
            </Field>
          </div>
          <Field label="Your endorsement" required>
            <Textarea
              data-testid="letter-input-quote"
              value={form.quote}
              onChange={update("quote")}
              rows={6}
              placeholder="A few sentences on why your organization supports the Baytown Bridge model."
              className="rounded-none border-[#CBD5E1] bg-white focus-visible:ring-0 focus-visible:border-[#0A192F]"
            />
          </Field>

          <Button
            type="submit"
            data-testid="letter-submit-btn"
            disabled={submitting}
            className="rounded-none w-full bg-[#0A192F] hover:bg-[#15243F] text-[#F9F7F1] h-12 font-mono text-xs uppercase tracking-widest"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting
              </>
            ) : (
              <>
                Submit Letter <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
        {label}
        {required && <span className="text-[#8B0000] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
